import * as THREE from 'three';

const MOVEMENT_KEYS = new Set(['w', 'a', 's', 'd', 'q', 'e']);

const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _move = new THREE.Vector3();
const _velocity = new THREE.Vector3();
const _targetVelocity = new THREE.Vector3();
const _mouseNdc = new THREE.Vector2();
const _lookPoint = new THREE.Vector3();
const _lookDir = new THREE.Vector3();
const _targetQuat = new THREE.Quaternion();
const _lookMatrix = new THREE.Matrix4();
const _raycaster = new THREE.Raycaster();

let _zoomVelocity = 0;
let _targetZoomVelocity = 0;

const isTypingTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
};

function clientToNdc(domElement, clientX, clientY, out) {
  const rect = domElement.getBoundingClientRect();
  const w = rect.width || 1;
  const h = rect.height || 1;
  out.x = ((clientX - rect.left) / w) * 2 - 1;
  out.y = -((clientY - rect.top) / h) * 2 + 1;
  return out;
}

function resolveLookPoint(camera, controls, _scene, mouseNdc) {
  _raycaster.setFromCamera(mouseNdc, camera);

  // Do not raycast the scene — PLY gaussian splats and dense meshes block the main thread.
  // Fly mode uses the mouse ray direction (same feel as standard FPS look).
  const dist = controls?.target
    ? Math.max(camera.position.distanceTo(controls.target), 1)
    : 10;

  return _lookPoint
    .copy(_raycaster.ray.origin)
    .add(_lookDir.copy(_raycaster.ray.direction).multiplyScalar(dist));
}

function rotateCameraTowardLookPoint(
  camera,
  controls,
  lookPoint,
  delta,
  lookRotateSpeed
) {
  _lookMatrix.lookAt(camera.position, lookPoint, camera.up);
  _targetQuat.setFromRotationMatrix(_lookMatrix);

  const blend = 1 - Math.exp(-lookRotateSpeed * Math.min(delta, 0.05));
  camera.quaternion.slerp(_targetQuat, blend);

  if (controls?.target) {
    const dist = Math.max(camera.position.distanceTo(controls.target), 1);
    camera.getWorldDirection(_lookDir);
    controls.target.copy(camera.position).add(_lookDir.multiplyScalar(dist));
  }
}

/**
 * Free camera: hold right-click + W/A/S/D/Q/E to enter (eye cursor).
 * While RMB is held: look toward cursor and fly with movement keys.
 * Scroll wheel adjusts fly speed; a brief HUD shows the new speed.
 * Release RMB to exit free camera completely.
 */
export function createCameraFreeMotion({
  getCamera,
  getControls,
  syncViewportCameras,
  getDomElement,
  getScene,
  getEnabled = () => true,
  // Soft, low defaults — motion eases in from near-zero rather than snapping to full speed.
  moveSpeed = 3.5,
  acceleration = 3.2,
  deceleration = 8,
  lookRotateSpeed = 3.2,
  minMoveSpeed = 0.5,
  maxMoveSpeed = 40,
}) {
  const pressedKeys = new Set();
  let rmbDown = false;
  let isFreeCamActive = false;
  let savedCursor = '';
  let lastPointer = { x: 0, y: 0 };
  /** RMB press position — mouse deltas are applied relative to canvas center from here. */
  let pointerOrigin = { x: 0, y: 0 };
  let canvasCenter = { x: 0, y: 0 };
  let lastPointerId = null;
  let controlsWereEnabled = true;
  /** Seconds since free-cam activated; used to ease initial move speed. */
  let freeCamAge = 0;
  let centerCursorEl = null;
  let speedHudEl = null;
  let speedHudHideTimer = null;
  /** Live fly speed — adjusted by mouse wheel while free cam is active. */
  let currentMoveSpeed = moveSpeed;

  const clearKeys = () => pressedKeys.clear();

  const formatSpeedLabel = (speed) =>
    `Speed ${Number(speed).toFixed(speed >= 10 ? 0 : 1)}`;

  const ensureSpeedHud = (domElement) => {
    if (speedHudEl || !domElement) return;
    const parent = domElement.parentElement || domElement;
    if (getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }
    speedHudEl = document.createElement('div');
    speedHudEl.setAttribute('data-free-cam-speed-hud', 'true');
    Object.assign(speedHudEl.style, {
      position: 'absolute',
      left: '50%',
      top: '18%',
      transform: 'translate(-50%, -50%)',
      padding: '6px 14px',
      borderRadius: '8px',
      background: 'rgba(18, 19, 22, 0.78)',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '13px',
      fontWeight: '600',
      letterSpacing: '0.02em',
      pointerEvents: 'none',
      zIndex: '21',
      opacity: '0',
      transition: 'opacity 160ms ease',
      whiteSpace: 'nowrap',
      userSelect: 'none',
    });
    parent.appendChild(speedHudEl);
  };

  const hideSpeedHud = () => {
    if (speedHudHideTimer) {
      clearTimeout(speedHudHideTimer);
      speedHudHideTimer = null;
    }
    if (speedHudEl) {
      speedHudEl.style.opacity = '0';
    }
  };

  const removeSpeedHud = () => {
    hideSpeedHud();
    if (speedHudEl?.parentElement) {
      speedHudEl.parentElement.removeChild(speedHudEl);
    }
    speedHudEl = null;
  };

  const showSpeedHud = (speed) => {
    const domElement = getDomElement?.();
    if (!domElement) return;
    ensureSpeedHud(domElement);
    if (!speedHudEl) return;

    speedHudEl.textContent = formatSpeedLabel(speed);
    // Force reflow so fade-in always plays when speed updates rapidly.
    speedHudEl.style.opacity = '0';
    void speedHudEl.offsetWidth;
    speedHudEl.style.opacity = '1';

    if (speedHudHideTimer) clearTimeout(speedHudHideTimer);
    speedHudHideTimer = setTimeout(() => {
      if (speedHudEl) speedHudEl.style.opacity = '0';
      speedHudHideTimer = null;
    }, 1200);
  };

  /** Snap look/cursor tracking to the canvas center so entry doesn't yank the view. */
  const centerPointerOnCanvas = (domElement) => {
    if (!domElement) return;
    const rect = domElement.getBoundingClientRect();
    canvasCenter.x = rect.left + rect.width * 0.5;
    canvasCenter.y = rect.top + rect.height * 0.5;
    lastPointer.x = canvasCenter.x;
    lastPointer.y = canvasCenter.y;
  };

  /** Map mouse from click-origin to look position anchored at canvas center. */
  const updateLookPointerFromClient = (clientX, clientY) => {
    lastPointer.x = canvasCenter.x + (clientX - pointerOrigin.x);
    lastPointer.y = canvasCenter.y + (clientY - pointerOrigin.y);
  };

  const showCenterCursor = (domElement) => {
    if (!domElement || centerCursorEl) return;
    const parent = domElement.parentElement || domElement;
    if (getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }
    centerCursorEl = document.createElement('div');
    centerCursorEl.setAttribute('data-free-cam-center-cursor', 'true');
    Object.assign(centerCursorEl.style, {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: '24px',
      height: '24px',
      marginLeft: '-12px',
      marginTop: '-12px',
      pointerEvents: 'none',
      zIndex: '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });
    centerCursorEl.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3" fill="#ffffff"/></svg>`;
    parent.appendChild(centerCursorEl);
  };

  const hideCenterCursor = () => {
    if (centerCursorEl?.parentElement) {
      centerCursorEl.parentElement.removeChild(centerCursorEl);
    }
    centerCursorEl = null;
  };

  const deactivateFreeCam = () => {
    const wasActive = isFreeCamActive;
    rmbDown = false;
    isFreeCamActive = false;
    clearKeys();
    _velocity.set(0, 0, 0);
    _zoomVelocity = 0;
    freeCamAge = 0;
    hideSpeedHud();

    const domElement = getDomElement?.();
    if (domElement) {
      hideCenterCursor();
      domElement.style.cursor = savedCursor;
      if (lastPointerId != null && domElement.releasePointerCapture) {
        try {
          domElement.releasePointerCapture(lastPointerId);
        } catch {
          /* already released */
        }
      }
      lastPointerId = null;
    }

    const controls = getControls?.();
    if (controls && wasActive) {
      controls.enabled = controlsWereEnabled;
    }
  };

  const activateFreeCam = () => {
    if (isFreeCamActive || !rmbDown || pressedKeys.size === 0) return;
    if (!getEnabled()) return;

    const domElement = getDomElement?.();
    if (!domElement) return;

    isFreeCamActive = true;
    freeCamAge = 0;
    savedCursor = domElement.style.cursor || '';
    // Center look target first so entry doesn't yank the view toward the click point.
    centerPointerOnCanvas(domElement);
    // Hide OS cursor and show a fixed eye reticle at canvas center.
    domElement.style.cursor = 'none';
    showCenterCursor(domElement);

    if (lastPointerId != null && domElement.setPointerCapture) {
      try {
        domElement.setPointerCapture(lastPointerId);
      } catch {
        /* ignore */
      }
    }

    const controls = getControls?.();
    if (controls) {
      controlsWereEnabled = controls.enabled;
      controls.enabled = false;
    }
  };

  const onKeyDown = (e) => {
    if (!getEnabled()) return;
    if (isTypingTarget(e.target)) return;
    if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;

    const key = e.key.toLowerCase();
    if (!MOVEMENT_KEYS.has(key)) return;

    pressedKeys.add(key);

    if (rmbDown) {
      activateFreeCam();
    }

    if (isFreeCamActive) {
      e.preventDefault();
    }
  };

  const onKeyUp = (e) => {
    pressedKeys.delete(e.key.toLowerCase());
  };

  const onRmbDown = (event) => {
    if (!getEnabled()) return;
    if (event.button !== 2) return;

    const domElement = getDomElement?.();
    if (!domElement || event.target !== domElement) return;

    event.preventDefault();
    event.stopPropagation();

    rmbDown = true;
    lastPointerId = event.pointerId;
    pointerOrigin.x = event.clientX;
    pointerOrigin.y = event.clientY;
    // Start aim at canvas center (not the click point) so entry feel is stable.
    centerPointerOnCanvas(domElement);

    activateFreeCam();
  };

  const onPointerMove = (event) => {
    if (!rmbDown && !isFreeCamActive) return;
    updateLookPointerFromClient(event.clientX, event.clientY);
  };

  const onPointerUp = (event) => {
    if (event.button !== 2 && event.pointerId !== lastPointerId) return;
    deactivateFreeCam();
  };

  const onContextMenu = (event) => {
    if (!getEnabled()) return;
    const domElement = getDomElement?.();
    if (!domElement || event.target !== domElement) return;
    event.preventDefault();
  };

  const onWheel = (event) => {
    if (!getEnabled()) return;
    if (!isFreeCamActive && !rmbDown) return;

    // Scroll adjusts fly speed instead of orbit-zoom while free cam / RMB is active.
    event.preventDefault();
    event.stopPropagation();

    const direction = event.deltaY < 0 ? 1 : -1;
    const factor = direction > 0 ? 1.12 : 1 / 1.12;
    const next = Math.min(
      maxMoveSpeed,
      Math.max(minMoveSpeed, currentMoveSpeed * factor)
    );

    // Ignore tiny no-ops at the clamp edges.
    if (Math.abs(next - currentMoveSpeed) < 1e-4) {
      showSpeedHud(currentMoveSpeed);
      return;
    }

    currentMoveSpeed = next;
    showSpeedHud(currentMoveSpeed);
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', deactivateFreeCam);

  const domElement = getDomElement?.();
  if (domElement) {
    domElement.addEventListener('pointerdown', onRmbDown, true);
    domElement.addEventListener('contextmenu', onContextMenu, true);
    domElement.addEventListener('pointermove', onPointerMove);
    domElement.addEventListener('pointerup', onPointerUp);
    domElement.addEventListener('pointercancel', onPointerUp);
    domElement.addEventListener('wheel', onWheel, { passive: false, capture: true });
  }
  window.addEventListener('pointerup', onPointerUp);

  const applyLookRotation = (delta) => {
    if (!isFreeCamActive) return;

    const domElement = getDomElement?.();
    const camera = getCamera();
    const controls = getControls();
    const scene = getScene?.();
    if (!domElement || !camera) return;

    clientToNdc(domElement, lastPointer.x, lastPointer.y, _mouseNdc);
    const lookPoint = resolveLookPoint(camera, controls, scene, _mouseNdc);
    rotateCameraTowardLookPoint(
      camera,
      controls,
      lookPoint,
      delta,
      lookRotateSpeed
    );

    if (typeof syncViewportCameras === 'function') {
      syncViewportCameras(camera);
    }

    controls?.dispatchEvent?.({ type: 'change' });
  };

  const apply = (delta) => {
    if (!getEnabled()) {
      _velocity.set(0, 0, 0);
      _zoomVelocity = 0;
      return;
    }

    if (!isFreeCamActive) {
      _velocity.set(0, 0, 0);
      _zoomVelocity = 0;
      return;
    }

    freeCamAge += delta;
    // Ease from ~25% speed up to full over ~0.7s so the first moments feel smooth/low.
    const speedRamp = Math.min(1, freeCamAge / 0.7);
    const easedRamp = speedRamp * speedRamp * (3 - 2 * speedRamp); // smoothstep
    const effectiveMoveSpeed = currentMoveSpeed * (0.25 + 0.75 * easedRamp);

    applyLookRotation(delta);

    const camera = getCamera();
    const controls = getControls();
    if (!camera) return;

    camera.updateMatrixWorld(true);

    _right.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
    _up.setFromMatrixColumn(camera.matrixWorld, 1).normalize();
    camera.getWorldDirection(_forward);

    const isOrtho = camera.isOrthographicCamera;

    _targetVelocity.set(0, 0, 0);
    _targetZoomVelocity = 0;

    if (pressedKeys.has('w')) {
      if (isOrtho) _targetZoomVelocity += 1;
      else _targetVelocity.add(_forward);
    }
    if (pressedKeys.has('s')) {
      if (isOrtho) _targetZoomVelocity -= 1;
      else _targetVelocity.sub(_forward);
    }
    if (pressedKeys.has('d')) _targetVelocity.add(_right);
    if (pressedKeys.has('a')) _targetVelocity.sub(_right);
    if (pressedKeys.has('e')) _targetVelocity.add(_up);
    if (pressedKeys.has('q')) _targetVelocity.sub(_up);

    if (_targetVelocity.lengthSq() > 0) {
      _targetVelocity.normalize().multiplyScalar(effectiveMoveSpeed);
      if (isOrtho && camera.zoom) {
        _targetVelocity.multiplyScalar(30 / camera.zoom);
      }
    }

    if (_targetZoomVelocity !== 0) {
      _targetZoomVelocity *= effectiveMoveSpeed * 0.5 * (camera.zoom || 1);
    }

    const isAccelerating =
      _targetVelocity.lengthSq() > 0 || _targetZoomVelocity !== 0;
    const smoothRate = isAccelerating ? acceleration : deceleration;
    const blend = 1 - Math.exp(-smoothRate * Math.min(delta, 0.05));
    _velocity.lerp(_targetVelocity, blend);
    _zoomVelocity += (_targetZoomVelocity - _zoomVelocity) * blend;

    if (_velocity.lengthSq() < 1e-8 && Math.abs(_zoomVelocity) < 1e-4) {
      return;
    }

    if (_velocity.lengthSq() > 1e-8) {
      _move.copy(_velocity).multiplyScalar(delta);
      camera.position.add(_move);
      if (controls?.target) {
        controls.target.add(_move);
      }
    }

    if (isOrtho && Math.abs(_zoomVelocity) > 1e-4) {
      camera.zoom += _zoomVelocity * delta;
      camera.zoom = Math.max(0.1, camera.zoom);
      camera.updateProjectionMatrix();
    }

    if (typeof syncViewportCameras === 'function') {
      syncViewportCameras(camera);
    }

    if (controls?.update) {
      controls.update();
    }
    controls?.dispatchEvent?.({ type: 'change' });
  };

  const dispose = () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', deactivateFreeCam);
    window.removeEventListener('pointerup', onPointerUp);

    const el = getDomElement?.();
    if (el) {
      el.removeEventListener('pointerdown', onRmbDown, true);
      el.removeEventListener('contextmenu', onContextMenu, true);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('wheel', onWheel, true);
      el.style.cursor = savedCursor;
    }

    hideCenterCursor();
    removeSpeedHud();
    deactivateFreeCam();
    _velocity.set(0, 0, 0);
    _zoomVelocity = 0;
  };

  return {
    apply,
    dispose,
    isMoving: () => isFreeCamActive && pressedKeys.size > 0,
    isLooking: () => isFreeCamActive,
    getMoveSpeed: () => currentMoveSpeed,
  };
}
